import { useEffect, useState, useCallback } from "react";

// Utils
import queryString from "query-string";
import { useLocation, useHistory } from "react-router-dom";

// FontAwesome
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import Tabs from "components/Common/Tabs/Tabs";
import ResultsHeader from "components/Screens/BQMQ/Results/ResultsHeader/ResultsHeader";
import SummaryCard from "components/Screens/BQMQ/Results/SummaryCard/SummaryCard";
import VotingSessionFilter from "components/Screens/BQMQ/Results/VotingSessionFilter/VotingSessionFilter";

// Utils
import {
    factors,
    getFactorOverallResult,
    getResults,
    isCoreResults,
    getAllCompanySessions,
} from "utils/bqmq.utils";

// Context
import { useBQMQ } from "context/BQMQ.context";
import { useSettings } from "context/Settings.context";

// Styles
import InvitedUserModal from "components/Screens/BQMQ/Results/InvitedUserModal/InvitedUserModal";
import CompanyResultDetailContainer from "./CompanyResultDetail.styles";

const getSummaryCards = (
    results: any,
    voteCountMode: string,
    setVoteCountMode: any
) => {
    return (
        <div className={"summaryRow"}>
            {factors.map(
                (factor: { name: string }) =>
                    factors &&
                    results && (
                        <SummaryCard
                            factor={factor.name}
                            results={results[factor.name]}
                            overallResult={getFactorOverallResult(
                                factor.name,
                                results
                            )}
                            usersVotes={results.votes}
                            comments={results.comments[factor.name]}
                            historicalComments={
                                results.historical_comments
                                    ? results.historical_comments[factor.name]
                                    : []
                            }
                            sessionDate={results?.session?.date}
                            key={factor.name}
                            voteCountMode={voteCountMode}
                            setVoteCountMode={setVoteCountMode}
                            editVote
                        />
                    )
            )}
        </div>
    );
};

export default function SessionResultDetail() {
    const { setShowLoadingToast } = useSettings();
    const { showFullUserList, setDefaultChoices } = useBQMQ();
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [sessionCoreResults, setSessionCoreResults] = useState<any>(null);
    const [sessionAllResults, setSessionAllResults] = useState<any>(null);
    const [voteCountMode, setVoteCountMode] = useState<"QUESTION" | "USER">(
        "QUESTION"
    );
    const [reloadKey, setReloadKey] = useState(0);

    const parsed = queryString.parse(location.search);
    const companyID = parsed.company_id;
    const sessionID = parsed.session_id || 0;

    const [selectedDateSession, setSelectedDateSession] = useState<string>("");
    const [companySessions, setCompanySessions] = useState<any>(null);
    const [gotCompanySessions, setGotCompanySessions] =
        useState<boolean>(false);

    const reload = useCallback(() => {
        setGotCompanySessions(false);
        setCompanySessions(null);
        setSessionAllResults(null);
        setSessionCoreResults(null);
        setSelectedDateSession("");
        setLoading(true);
        setReloadKey((k) => k + 1);
    }, []);

    // Once results load, patch the URL if company_id was missing/wrong, then fetch sessions.
    useEffect(() => {
        if (sessionAllResults === null) return;
        const resultsCompanyId = sessionAllResults?.session?.company?.id;
        if (!resultsCompanyId) {
            // Results resolved but contained no valid company (e.g. invalid ID) — show no sessions
            setCompanySessions({ data: [], total: 0 });
            setGotCompanySessions(true);
            return;
        }

        if (String(companyID) !== String(resultsCompanyId)) {
            const params = new URLSearchParams(location.search);
            params.set("company_id", resultsCompanyId);
            history.replace({ search: params.toString() });
        }

        setGotCompanySessions(false);
        setCompanySessions(null);
        getAllCompanySessions(
            (sessions: any) => {
                setCompanySessions(sessions);
                setGotCompanySessions(true);
            },
            resultsCompanyId,
            sessionID
        );
    }, [sessionAllResults]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // No company or session — redirect to BQMQ home
        if (!companyID && !sessionID) {
            history.replace("/company/voting");
            return;
        }
        if (selectedDateSession !== "") {
            getResults(
                companyID,
                selectedDateSession,
                setDefaultChoices,
                setSessionAllResults,
                setSessionCoreResults
            );
        } else {
            setShowLoadingToast({
                show: true,
                message: "LOADING COMPANY RESULTS",
            });
            getResults(
                companyID,
                sessionID,
                setDefaultChoices,
                setSessionAllResults,
                setSessionCoreResults
            );
            setLoading(false);
            setShowLoadingToast({
                show: false,
                message: "LOADING COMPANY RESULTS",
            });
        }
    }, [
        selectedDateSession,
        companyID,
        sessionID,
        reloadKey,
        history,
        setDefaultChoices,
        setShowLoadingToast,
    ]);

    const noSessions = (companySessions?.total ?? 0) === 0;

    if (!gotCompanySessions) {
        return (
            <CompanyResultDetailContainer
                className={"pageContainer fullPageLoading"}
            >
                <LoadingSpinner />
            </CompanyResultDetailContainer>
        );
    }

    return (
        <CompanyResultDetailContainer className={"pageContainer"}>
            <ResultsHeader reload={reload} sessionData={sessionAllResults} />
            {noSessions ? (
                <div className="noData">
                    <FontAwesomeIcon icon={faInfoCircle} fixedWidth />
                    <p>No voting sessions found for the selected company.</p>
                </div>
            ) : (
                <>
                    <VotingSessionFilter
                        setSelectedDateSession={setSelectedDateSession}
                        allCompanySessions={companySessions}
                        gotCompanySessions={gotCompanySessions}
                    />
                    <div className="res-container">
                        {!loading ? (
                            <Tabs
                                defaultTab="ALL"
                                dontChangeUrl
                                tabs={[
                                    {
                                        title: "ALL",
                                        value: "ALL",
                                        content: getSummaryCards(
                                            sessionAllResults,
                                            voteCountMode,
                                            setVoteCountMode
                                        ),
                                    },
                                    {
                                        title: "CORE",
                                        value: "CORE",
                                        content: getSummaryCards(
                                            sessionCoreResults,
                                            voteCountMode,
                                            setVoteCountMode
                                        ),
                                        hide: !isCoreResults(
                                            sessionCoreResults
                                        ),
                                    },
                                ]}
                            />
                        ) : (
                            <div className={"loadingScreen"}>
                                <LoadingSpinner />
                            </div>
                        )}

                        {showFullUserList && (
                            <InvitedUserModal
                                companyID={sessionAllResults.session.id}
                                session={sessionAllResults}
                            />
                        )}
                    </div>
                </>
            )}
        </CompanyResultDetailContainer>
    );
}
